/**
 * Article text extraction for TTS narration.
 *
 * Walks the article body (`.xot-article__body`) with an explicit allow-list
 * of paragraph-level tags (p, h2-h6, li, blockquote) and a wide deny-list
 * for ads, embeds, related-link blocks, premium gates, reactions,
 * pull-quote attributions, and share buttons.
 *
 * Joins paragraphs with double newlines so the TTS engine (ElevenLabs /
 * Azure / SpeechSynthesis) gets natural paragraph pauses — much more
 * natural cadence than one long blob of text.
 *
 * Replaces the inline `getArticleText()` previously hard-coded in
 * `assets/js/single-article.js` (~60 LOC). The DENY_SELECTORS array is
 * exported so future bites (highlight-map sentence walker) can share the
 * same exclusion rules.
 *
 * Build path:
 *   src   assets/js/src/single-article/text-extract.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/text-extract.js  (attaches window.XOT.article.text)
 *
 * Test path:
 *   src   assets/js/src/single-article/text-extract.ts
 *   ↓ vitest (transforms TS, jsdom env)
 *   test  tests/js/single-article-text-extract.test.ts
 *
 * @since Phase 2 — JS modernization, bite 3
 */

/* The element selectors removed from a cloned article body before
   text extraction. Frozen + exported so the upcoming highlight-map
   module shares the same exclusion contract — no drift over time. */
export const DENY_SELECTORS: ReadonlyArray<string> = Object.freeze([
    // ads + monetisation
    '.xot-ad', '.xot-ad-slot', '.xot-ad-wrapper', '.xot-ads',
    // gates + premium
    '.xot-premium-gate', '.xot-pro-gate', '.xot-newsletter', '.xot-newsletter-gate',
    // related / next-story / reactions / takes / share buttons
    '.xot-related', '.xot-related-artist', '.xot-related-link',
    '.xot-next-story', '.xot-take', '.xot-takes', '.xot-ai-take',
    '.xot-reactions', '.xrb', '.xot-share-wrap', '.xot-btn--listen',
    // figures, captions, embeds, media
    'figure', 'figcaption', 'iframe', 'video', 'audio',
    '.wp-block-embed', '.wp-block-audio', '.wp-block-video',
    '.wp-block-image', '.wp-caption',
    // structural noise
    'script', 'style', 'noscript', 'template',
    // explicit opt-out anywhere downstream
    '[data-xot-tts="skip"]', '[aria-hidden="true"]', '[hidden]',
]);

const ALLOW_TAGS = 'p, h2, h3, h4, h5, h6, li, blockquote';
const MIN_PARA_LEN = 4;          // skip "Hi." / "...", keep real prose
const PARA_SEP     = '\n\n';     // ElevenLabs reads this as a natural pause

/**
 * Extract narration-ready text from an article body element.
 *
 * Pure — clones the input element before mutating; safe to call repeatedly.
 *
 * @param body  The article body element (typically `.xot-article__body`)
 * @returns     Plain text with paragraphs joined by `\n\n`, or `''` if empty
 *
 * @example
 *   const body = document.querySelector('.xot-article__body');
 *   const text = extractArticleText(body);
 */
export function extractArticleText(body: Element | null | undefined): string {
    if (!body) return '';
    const clone = body.cloneNode(true) as Element;
    for (const sel of DENY_SELECTORS) {
        clone.querySelectorAll(sel).forEach(n => n.remove());
    }
    const parts: string[] = [];
    clone.querySelectorAll(ALLOW_TAGS).forEach(n => {
        const raw = (n as HTMLElement).innerText ?? n.textContent ?? '';
        const t   = raw.replace(/\s+/g, ' ').trim();
        if (t.length >= MIN_PARA_LEN) parts.push(t);
    });
    return parts.join(PARA_SEP).trim();
}

/* Cache keyed by the article body element. If the DOM swaps in a different
   `.xot-article__body` (SPA-style nav) the cache invalidates automatically. */
let _cachedText: string | null = null;
let _cachedFor:  Element | null = null;

/**
 * Get cached extracted text for the current document's article body.
 * Computes once and reuses; O(1) on subsequent calls.
 *
 * Returns `''` when no `.xot-article__body` exists (e.g., on non-article
 * pages where the script happens to be loaded — safe no-op).
 */
export function getCachedArticleText(): string {
    if (typeof document === 'undefined') return '';
    const body = document.querySelector('.xot-article__body');
    if (!body) {
        _cachedText = '';
        _cachedFor  = null;
        return '';
    }
    if (_cachedText !== null && _cachedFor === body) return _cachedText;
    _cachedText = extractArticleText(body);
    _cachedFor  = body;
    return _cachedText;
}

/**
 * Reset the extraction cache. Call after dynamic content insertion (e.g.,
 * the autolinker swapping in href anchors) when stale text matters.
 */
export function resetTextCache(): void {
    _cachedText = null;
    _cachedFor  = null;
}

/* ── window.XOT.article.text attachment (legacy IIFE consumers) ──────
   single-article.js (top-level IIFE, body-deferred) will read from this
   surface via `(window.XOT?.article?.text?.getCached)` with a thin
   inline fallback for cache-lag safety. Same pattern as xot-fmt (Bite 1).
*/
interface XotArticleText {
    extract: typeof extractArticleText;
    getCached: typeof getCachedArticleText;
    reset: typeof resetTextCache;
    denySelectors: ReadonlyArray<string>;
}

declare global {
    interface Window {
        XOT?: {
            article?: { text?: XotArticleText };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.text = {
        extract:       extractArticleText,
        getCached:     getCachedArticleText,
        reset:         resetTextCache,
        denySelectors: DENY_SELECTORS,
    };
    xot.article = article;
    window.XOT  = xot;
}
